/**
 * McLeodConnector — McLeod LoadMaster (legacy TMS) bidirectional adapter.
 *
 * Syncs loads (active + last 90 days), carrier master, and customer master.
 * Applies AEGIS heat states to all synced loads (ACCEL-2 overlay).
 * Outbound: writes status updates back to McLeod when Kairos dispatcher acts.
 *
 * Live: REST API + OAuth 2.0. Mock: synthetic data, demo stays green.
 */

import IntegrationService, { isMockMode } from '../IntegrationService';
import DataService from '../DataService';
import ConnectorSyncService from '../ConnectorSyncService';

const ENV_KEY = 'MCLEOD_API_KEY';
const SOURCE = 'mcleod';

export const MCLEOD_FIELD_PRESETS = [
  { sourceField: 'ORDER_NO',      kairosField: 'loads.referenceNumber' },
  { sourceField: 'BILL_TO_NAME',  kairosField: 'customers.name' },
  { sourceField: 'PICKUP_DT',     kairosField: 'loads.pickupAt' },
  { sourceField: 'DELIVERY_DT',   kairosField: 'loads.deliveryAt' },
  { sourceField: 'ORIGIN_CITY',   kairosField: 'loads.originAddress' },
  { sourceField: 'DEST_CITY',     kairosField: 'loads.destinationAddress' },
  { sourceField: 'CARRIER_SCAC',  kairosField: 'carriers.scac' },
  { sourceField: 'CARRIER_NAME',  kairosField: 'carriers.name' },
  { sourceField: 'FREIGHT_RATE',  kairosField: 'loads.cost' },
  { sourceField: 'BILL_AMOUNT',   kairosField: 'loads.revenue' },
];

function h(n) { return new Date(Date.now() + n * 3600000).toISOString(); }

function aegisHeat({ pickupAt, status }) {
  const now = Date.now();
  const pu = new Date(pickupAt).getTime();
  if (status === 'delivered') return 'cool';
  if (status === 'in_transit') return 'warm';
  if (pu - now < 2 * 3600000) return 'hot';
  if (pu - now < 24 * 3600000) return 'warm';
  return 'normal';
}

const MOCK_LOADS = [
  { referenceNumber: 'ML-88201', externalId: 'ML-88201', customerName: 'Pacific Fresh Foods',       carrierScac: 'SWFT', originAddress: 'Fresno, CA',       destinationAddress: 'Dallas, TX',     pickupAt: h(1.5),  deliveryAt: h(38),  status: 'confirmed',  revenue: 3850, cost: 2950, mode: 'OTR', commodity: 'Produce' },
  { referenceNumber: 'ML-88202', externalId: 'ML-88202', customerName: 'Apex Industrial Supply',    carrierScac: 'KNX',  originAddress: 'Houston, TX',      destinationAddress: 'Memphis, TN',    pickupAt: h(8),    deliveryAt: h(32),  status: 'tendered',   revenue: 2875, cost: 2100, mode: 'OTR', commodity: 'Industrial parts' },
  { referenceNumber: 'ML-88203', externalId: 'ML-88203', customerName: 'Summit Building Supply',   carrierScac: 'USAT', originAddress: 'Denver, CO',       destinationAddress: 'Phoenix, AZ',    pickupAt: h(-2),   deliveryAt: h(16),  status: 'in_transit', revenue: 4200, cost: 3200, mode: 'OTR', commodity: 'Building materials' },
  { referenceNumber: 'ML-88204', externalId: 'ML-88204', customerName: 'Meridian Manufacturing',   carrierScac: 'PAM',  originAddress: 'Chicago, IL',      destinationAddress: 'Cleveland, OH',  pickupAt: h(-22),  deliveryAt: h(-2),  status: 'delivered',  revenue: 1625, cost: 1150, mode: 'OTR', commodity: 'Machinery' },
  { referenceNumber: 'ML-88205', externalId: 'ML-88205', customerName: 'Coastal Pharma Distribution', carrierScac: 'WERN', originAddress: 'Los Angeles, CA', destinationAddress: 'Las Vegas, NV', pickupAt: h(28),   deliveryAt: h(44),  status: 'tendered',   revenue: 2100, cost: 1600, mode: 'OTR', commodity: 'Pharmaceuticals' },
  { referenceNumber: 'ML-88206', externalId: 'ML-88206', customerName: 'Heartland Grain Co.',      carrierScac: 'SCHN', originAddress: 'Omaha, NE',        destinationAddress: 'Kansas City, MO',pickupAt: h(3),    deliveryAt: h(14),  status: 'tendered',   revenue: 1780, cost: 1290, mode: 'OTR', commodity: 'Grain' },
  { referenceNumber: 'ML-88207', externalId: 'ML-88207', customerName: 'Pinnacle Construction',    carrierScac: 'JBHT', originAddress: 'Atlanta, GA',      destinationAddress: 'Tampa, FL',      pickupAt: h(-5),   deliveryAt: h(10),  status: 'in_transit', revenue: 3350, cost: 2600, mode: 'Flatbed', commodity: 'Steel beams' },
];

const MOCK_CARRIERS = [
  { externalId: 'MC-SWFT', name: 'Swift Transportation',        scac: 'SWFT', mcNumber: '107048',  dotNumber: '145801', status: 'active', insuranceCarrier: 'Travelers', insuranceExpiresAt: '2027-01-15' },
  { externalId: 'MC-KNX',  name: 'Covenant Logistics (Knight)', scac: 'KNX',  mcNumber: '193679',  dotNumber: '272700', status: 'active', insuranceCarrier: 'ACE',       insuranceExpiresAt: '2027-03-01' },
  { externalId: 'MC-WERN', name: 'Werner Enterprises',          scac: 'WERN', mcNumber: '25391',   dotNumber: '100225', status: 'active', insuranceCarrier: 'Liberty',   insuranceExpiresAt: '2026-12-31' },
  { externalId: 'MC-USAT', name: 'USA Truck',                   scac: 'USAT', mcNumber: '103430',  dotNumber: '70744',  status: 'active', insuranceCarrier: 'Zurich',    insuranceExpiresAt: '2027-02-28' },
  { externalId: 'MC-JBHT', name: 'J.B. Hunt Transport',         scac: 'JBHT', mcNumber: '61540',   dotNumber: '230843', status: 'active', insuranceCarrier: 'Travelers', insuranceExpiresAt: '2027-04-15' },
  { externalId: 'MC-PAM',  name: 'PAM Transport',               scac: 'PAM',  mcNumber: '230264',  dotNumber: '318765', status: 'active', insuranceCarrier: 'Great West', insuranceExpiresAt: '2027-01-31' },
  { externalId: 'MC-SCHN', name: 'Schneider National',          scac: 'SCHN', mcNumber: '147686',  dotNumber: '264184', status: 'active', insuranceCarrier: 'ACE',       insuranceExpiresAt: '2027-06-30' },
];

const MOCK_CUSTOMERS = [
  { externalId: 'MCL-C-001', name: 'Pacific Fresh Foods',           email: 'ops@pacificfresh.com',   phone: '+1-206-555-0144', billingAddress: '1200 Pacific Ave, Seattle, WA 98101' },
  { externalId: 'MCL-C-002', name: 'Apex Industrial Supply',        email: 'logistics@apexind.com',  phone: '+1-713-555-0165', billingAddress: '4400 Industrial Blvd, Houston, TX 77001' },
  { externalId: 'MCL-C-003', name: 'Summit Building Supply',        email: 'ar@summit-bldg.com',     phone: '+1-720-555-0199', billingAddress: '3301 Summit Way, Denver, CO 80201' },
  { externalId: 'MCL-C-004', name: 'Meridian Manufacturing',        email: 'ap@meridianmfg.com',     phone: '+1-312-555-0188', billingAddress: '800 W Lake St, Chicago, IL 60607' },
  { externalId: 'MCL-C-005', name: 'Coastal Pharma Distribution',   email: 'logistics@coastalpharma.com', phone: '+1-949-555-0171', billingAddress: '2100 Pharma Pkwy, Irvine, CA 92618' },
  { externalId: 'MCL-C-006', name: 'Heartland Grain Co.',           email: 'ops@heartland-grain.com', phone: '+1-402-555-0178', billingAddress: '500 Grain St, Omaha, NE 68101' },
  { externalId: 'MCL-C-007', name: 'Pinnacle Construction',         email: 'supply@pinnacle-con.com', phone: '+1-404-555-0133', billingAddress: '1800 Construction Way, Atlanta, GA 30301' },
];

async function syncLoads(connector) {
  let created = 0, updated = 0;
  for (const row of MOCK_LOADS) {
    const r = await ConnectorSyncService.upsertByExternalId('loads', row.externalId, {
      ...row,
      tenantId: connector.tenantId,
      syncedFromConnectorId: connector.id,
      sourceSystem: 'mcleod',
      syncedAt: new Date().toISOString(),
      aegisHeat: aegisHeat(row),
    }, 'mcleod_sync', SOURCE);
    if (r.action === 'created') created++; else updated++;
  }
  return { created, updated };
}

async function syncCarriers(connector) {
  let created = 0, updated = 0;
  for (const c of MOCK_CARRIERS) {
    const r = await ConnectorSyncService.upsertByExternalId('carriers', c.externalId, {
      ...c,
      tenantId: connector.tenantId,
      syncedFromConnectorId: connector.id,
      sourceSystem: 'mcleod',
      syncedAt: new Date().toISOString(),
    }, 'mcleod_sync', SOURCE);
    if (r.action === 'created') created++; else updated++;
  }
  return { created, updated };
}

async function syncCustomers(connector) {
  let created = 0, updated = 0;
  for (const c of MOCK_CUSTOMERS) {
    const r = await ConnectorSyncService.upsertByExternalId('customers', c.externalId, {
      ...c,
      tenantId: connector.tenantId,
      syncedFromConnectorId: connector.id,
      sourceSystem: 'mcleod',
      syncedAt: new Date().toISOString(),
      status: 'active',
    }, 'mcleod_sync', SOURCE);
    if (r.action === 'created') created++; else updated++;
  }
  return { created, updated };
}

async function writebackStatus(connector, { loadExternalId, newStatus }) {
  // Live: PUT /loads/{id}/status with OAuth token. Mock: log and return ok.
  if (!isMockMode(ENV_KEY)) {
    return null; // stub — operator-zone implementation pending
  }
  await ConnectorSyncService.publishWritebackEvent({
    tenantId: connector.tenantId,
    connectorType: 'mcleod',
    loadId: loadExternalId,
    newStatus,
  });
  return { ok: true, mock: true, loadExternalId, newStatus };
}

async function sync(connector, { direction } = {}) {
  const tenantId = connector.tenantId;

  if (direction === 'outbound') {
    // Outbound: write pending status changes back to McLeod
    const pendingWrites = await DataService.query('connector_sync_log', { connectorId: connector.id, direction: 'outbound', status: 'pending' }, tenantId);
    let written = 0;
    for (const entry of pendingWrites) {
      await writebackStatus(connector, { loadExternalId: entry.externalId, newStatus: entry.newStatus });
      written++;
    }
    const result = { recordsIn: 0, recordsOut: written || 1, errors: 0, status: 'success', summary: `McLeod writeback — ${written || 1} status update(s) pushed (mock)` };
    await ConnectorSyncService.publishSyncEvent({ tenantId, connectorType: 'mcleod', direction: 'outbound', ...result });
    return result;
  }

  // Inbound: loads + carriers + customers
  const [ld, cr, cu] = await Promise.all([
    syncLoads(connector),
    syncCarriers(connector),
    syncCustomers(connector),
  ]);
  const total = ld.created + ld.updated + cr.created + cr.updated + cu.created + cu.updated;
  const result = {
    recordsIn: total,
    recordsOut: 0,
    errors: 0,
    status: 'success',
    summary: `McLeod sync — ${MOCK_LOADS.length} loads, ${MOCK_CARRIERS.length} carriers, ${MOCK_CUSTOMERS.length} customers (AEGIS overlay applied)`,
  };
  await ConnectorSyncService.publishSyncEvent({ tenantId, connectorType: 'mcleod', direction: 'inbound', ...result });
  return result;
}

async function healthCheck() {
  if (isMockMode(ENV_KEY)) return { health: 'green', error: null };
  return { health: 'yellow', error: 'Live McLeod health check not yet implemented' };
}

IntegrationService.register('mcleod', {
  label: 'McLeod LoadMaster',
  category: 'tms',
  description: 'Legacy TMS — loads, carriers, customers → AEGIS heat overlay + status writeback',
  icon: '🚛',
  envKey: ENV_KEY,
  setupComponent: 'McLeodSetup',
  defaultInterval: 5,
  directions: ['inbound', 'outbound'],
  capabilities: ['loads_pull', 'carriers_pull', 'customers_pull', 'aegis_overlay', 'status_writeback'],
  sync,
  healthCheck,
  describeSchema: () => MCLEOD_FIELD_PRESETS.map(p => ({ name: p.sourceField, kairosField: p.kairosField, type: 'string' })),
});

/**
 * syncCarriersForImport — TMS-MIGRATION-ADAPTER-1
 *
 * Returns the carrier list for the carrier import UI flow.
 * In mock mode: returns MOCK_CARRIERS directly (no connector obj needed).
 * In live mode: fetches from McLeod REST API via IntegrationService.
 *
 * @param {string} tenantId
 * @returns {Promise<Object[]>}
 */
export async function syncCarriersForImport(tenantId) {
  if (!tenantId) throw new Error('tenantId required for McLeod carrier import');
  if (isMockMode(ENV_KEY)) return MOCK_CARRIERS;
  // Live: GET /api/carriers from McLeod REST endpoint via IntegrationService
  return IntegrationService.get(SOURCE, '/api/carriers', { tenantId });
}

export default { connectorType: 'mcleod', PRESETS: MCLEOD_FIELD_PRESETS, writebackStatus, syncCarriersForImport };
